import React from "react";
// import SVLS_API from "../../../api/api-services/svls-api";
import {
  BookmakerDTO,
  BookmakerRunnerDTO,
  checkIncludeMultiMarket,
  CURRENCY_TYPE_FACTOR,
  EventDTO,
  useAppDispatch,
  getCurrencyTypeFromToken,
  useAppSelector,
  langData,
  setLoading,
  showToast,
  useBookmaker,
  isBackOnlyMarket,
  isBmSpecialMarket
} from "@hypexone/uicore";
import {
  IMultiMarket,
  useMarketLocalState,
} from "../../../../utils/hooks/storage.hooks";
import MarketTableHeading from "../market-table/MarketTableHeading";
import BookmakerRow from "./BookmakerRow";
import { isMobile } from "react-device-detect";
import Modal from "../../shared/ModalComponent";
import PinIconGray from "../../../../assets/icons/events-icons/pin-icon-gray.svg";
import PinIconGreen from "../../../../assets/icons/events-icons/pin-icon-green.svg";
import InfoIconRoundedWhite from "../../../../assets/icons/info-icon-rounded-white.svg";
import Button from "../../shared/Button/Button";
import { colors } from "../../../../styles/colors";
import { BRAND_DOMAIN } from "../../../../constants/Branding";
import { API_CLIENT } from "../../../../App.utils";
import OneClickBettingCountdown from "../../one-click-betting/OneClickBettingCountdown";

export interface OddsInfoMsg {
  launch: boolean;
  oddsType: string | null;
  eventTypeID: string | null;
}

interface BMProps {
  eventData: EventDTO;
  bmData: BookmakerDTO[];
  loggedIn: boolean;
  getFormattedMinLimit: (num: number) => string;
  getFormattedMaxLimit: (num: number) => string;
  isMultiMarket?: boolean;
  exposureMap: any;
  setBetStartTime?: Function;
  setAddNewBet?: Function;
  loading: boolean;
}

// const multiPinsMap = {
//   purple: RemoveMultiPin,
//   darkvoilet: RemoveMultiPinDarkViolet,
//   darkgreen: RemoveMultiPinDarkGreen,
// };

const Bookmaker: React.FC<BMProps> = ({
  eventData,
  bmData,
  setAddNewBet,
  setBetStartTime,
  isMultiMarket,
  loggedIn,
  exposureMap,
  loading,
}) => {
  const dispatch = useAppDispatch();
  const {
    bets,
    openBets,
    isOneClickBettingLoading,
    bettingInprogress,
    cashoutInProgress,
    betStatusResponse,
  } = useAppSelector((state) => state.betSlip);
  const [multiMarketData, setMultiMarketData] = useMarketLocalState();
  const cFactor = CURRENCY_TYPE_FACTOR[getCurrencyTypeFromToken()];

  // Initialize useBookmaker hook
  const hookResult = useBookmaker({
    eventData,
    bmData,
    exposureMap,
    bets,
    openBets,
    cashoutInProgress,
    betStatusResponse,
    loggedIn,
    isMultiMarket,
    API: API_CLIENT,
    setBetStartTime: setBetStartTime as ((date: Date) => void) | undefined,
    setAddNewBet: setAddNewBet as ((val: boolean) => void) | undefined,
    multiMarketData,
    setMultiMarketData,
    bettingInprogress,
    isOneClickBettingLoading,
  });

  // Destructure hook values
  const {
    confirmCashout,
    setConfirmCashout,
    confirmTurboCashout,
    setConfirmTurboCashout,
    coMarket,
    setCoMarket,
    turboCoMarket,
    setTurboCoMarket,
    turboCashoutLoading,
    isPopupVisible,
    openPopup,
    closePopup,
    disabledStatus,
    calCashout,
    isHaveCashOut,
    autoCashout: hookAutoCashout,
    handleMultiMarket,
    getMarketAlerts,
    getCashoutState,
    getCashoutDisabled,
    getOutcomeId,
    getOpenBetsPLInArray,
    getTotalPL,
    isLossCutAvailable,
    getLossCutProfit,
    getLossCutDisabled,
    autoLossCut,
    isTurboCashoutAvailable,
    getSpeedCashCountdown,
    turboCashoutFunction,
    getTurboCashoutData,
    getTurboInfo,
  } = hookResult;

  // Wrapper for autoCashout to match Laser247's signature (no params)
  const autoCashout = async () => {
    if (coMarket == null) return;
    await hookAutoCashout(coMarket);
  };

  const rules = [
    "advantage_or_disadvantage_for_team_txt",
    "suspend_or_void_rights",
    "compare_teams_25_overs",
    "compare_teams_10_overs",
    "contacted_within_7_days",
    "end_two_teams_with_equal_points",
    "tennis_advance_fancy_market",
    "all_bets_will_be_voided",
    "market_will_be_settled_as_three_sets",
    "virtual_cricket",
    "technical_issues_bookmaker_market_will_be_voided",
  ];
  const highLightedIndices = [0, 1, 2, 3, 4, 5, 6, 9];

  return (
    <div className="flex flex-col  rounded-md border border-solid border-neutral-200 max-md:max-w-full">
      {bmData?.map((market, index) => {
        const {
          isAvailable: isTurboAvailable,
          label: turboLabel,
          countdown: countdownVal,
        } = getTurboInfo(market);
        const {
          isProfitable,
          label: cashoutLabel,
          hasCashout,
        } = getCashoutState(market);
        const cashoutDisabled =
          getCashoutDisabled(market) || eventData?.eventSuspended === true;
        const backOnly = isBmSpecialMarket(market.marketName, market.oddType);
        const isTossMarket = market.marketName.toLowerCase().includes("toss");
        const showButtons =
          (market?.runners?.length === 2) &&
          !backOnly && !isBmSpecialMarket(market.marketName, market.oddType) && !isTossMarket;

        const buttonsJSX = showButtons ? (
          <div className="flex items-center gap-1 flex-wrap">
            {isTurboAvailable ? (
              (eventData?.speedCashEnabled && <Button
                className="px-2 tracking-tighter text-white leading-normal relative overflow-hidden text-[10px] transition duration-150 ease-in-out bg-[#5bb450] border border-[#7dd970] rounded-md text-center shadow-[inset_-12px_-8px_40px_#46464620] font-mono font-bold flex items-center justify-center flex-row h-[34px] cursor-pointer"
                disabled={
                  !isTurboAvailable ||
                  disabledStatus.includes(
                    market.status?.toLowerCase() ?? "",
                  ) ||
                  market.suspend === true ||
                  eventData?.eventSuspended === true ||
                  !(
                    `${market?.marketId}:${market?.marketName}` in
                    exposureMap
                  ) ||
                  turboCashoutLoading ||
                  countdownVal > 0
                }
                loading={loading}
                onClick={() => {
                  if (countdownVal > 0) return;
                  setTurboCoMarket(market);
                  setConfirmTurboCashout(true);
                }}
              >
                {turboLabel}
              </Button>)
            ) : (
              (eventData?.cashoutEnabled && <Button
                className={`px-2 tracking-tighter text-white leading-normal relative overflow-hidden text-[10px] ml-1 transition duration-150 ease-in-out bg-[#701920] border border-[#9a2535] rounded-md text-center shadow-[inset_-12px_-8px_40px_#46464620] font-mono font-bold flex items-center justify-center flex-row h-fit py-1 cursor-pointer ${
                  isProfitable ? "profit" : "loss"
                }`}
                disabled={cashoutDisabled}
                loading={loading}
                onClick={() => {
                  setCoMarket(market);
                  setConfirmCashout(true);
                }}
              >
                {langData?.["cashout"].toUpperCase()}{cashoutLabel ? cashoutLabel.replace(/\s*:\s*/, ':') : ''}
              </Button>)
            )}
            {eventData?.losscutEnabled && <Button
              className="loss-cut-btn px-2 tracking-tighter text-white leading-normal relative overflow-hidden text-[10px] transition duration-150 ease-in-out border border-[#7dd970] rounded-md text-center shadow-[inset_-12px_-8px_40px_#46464620] font-bold flex items-center justify-center flex-row h-fit py-1 cursor-pointer"
              style={{ background: "#5bb450", color: "#fff" }}
              disabled={
                bettingInprogress ||
                (getLossCutDisabled && getLossCutDisabled(market)) ||
                (cashoutInProgress?.loading &&
                  cashoutInProgress?.marketId === market.marketId)
              }
              loading={
                loading ||
                (cashoutInProgress?.loading &&
                  cashoutInProgress?.marketId === market.marketId)
              }
              onClick={() => autoLossCut && autoLossCut(market)}
            >
              {langData?.["loss_cut"] || "Loss Cut"}
              {typeof getLossCutProfit === "function" &&
              getLossCutProfit(market) != null
                ? ` : ₹${Number(getLossCutProfit(market)).toFixed(2)}`
                : ""}
            </Button>}
          </div>
        ) : null;

        return (
          <React.Fragment key={index}>
            {!isMultiMarket ||
            checkIncludeMultiMarket(multiMarketData, market.marketId) ? (
              <>
                <div className="relative mb-[1rem]">
                  {getMarketAlerts(market).showCountdown && (
                    <OneClickBettingCountdown
                      delay={market?.marketLimits?.delay || 0}
                    />
                  )}

                  <div className="flex items-center justify-between w-full bg-blue4 px-4 py-2 text-xs text-white font-bold">
                    <div className="flex items-center">
                      {!checkIncludeMultiMarket(
                        multiMarketData,
                        market.marketId,
                      ) ? (
                        <span title="Add To Multi Markets">
                          <img
                            src={PinIconGray}
                            alt="multipin"
                            className="multi-add-icon cursor-pointer max-h-[22px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              multiMarketData?.length < 10 &&
                                handleMultiMarket(
                                  eventData?.competitionId,
                                  eventData?.eventId,
                                  market.marketId,
                                  eventData?.providerName,
                                  eventData?.sportId,
                                  true,
                                );
                            }}
                          />
                        </span>
                      ) : (
                        <span title="Remove From Multi Markets">
                          <img
                            src={PinIconGreen}
                            alt="multipin"
                            className="multi-remove-icon cursor-pointer max-h-[22px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMultiMarket(
                                eventData?.competitionId,
                                eventData?.eventId,
                                market.marketId,
                                eventData?.providerName,
                                eventData?.sportId,
                                false,
                              );
                            }}
                          />
                        </span>
                      )}

                      <div className="ml-2 flex items-center">
                        <div>{market?.marketName?.toUpperCase()}</div>
                        {/* Desktop-only buttons (hidden on mobile) */}
                        <div className="hidden md:block">{buttonsJSX}</div>
                        <div className="ml-4 cursor-pointer">
                          <img
                            onClick={openPopup}
                            src={InfoIconRoundedWhite}
                            alt="info-icon"
                            className="h-[13px] w-[13px] min-w-[13px]"
                          />
                        </div>
                      </div>
                      <span>
                        {isMultiMarket && !isMobile && (
                          <span className="pl-2">{eventData?.eventName}</span>
                        )}
                      </span>
                    </div>
                    <Modal
                      isOpen={isPopupVisible}
                      onClose={closePopup}
                      content={<div>Popup Content</div>}
                      title="Rules"
                    >
                      {rules.map((point, index) => (
                        <div
                          key={index}
                          className={`p-[8px] border-b font-sans font-[450] border-gray-300 flex items-center text-[12px]  ${
                            highLightedIndices.includes(index)
                              ? "text-[#F1556C]"
                              : " text-[#000]"
                          }`}
                        >
                          {langData?.[point] || point}
                        </div>
                      ))}
                    </Modal>
                  </div>

                  {/* Mobile-only buttons strip */}
                  {showButtons && (
                    <div className="md:hidden flex items-center justify-end px-4 py-2 bg-blue4 w-full gap-2">
                      {buttonsJSX}
                    </div>
                  )}

                  <table className="w-full text-[14px] font-medium text-black bg-yellow1 [&_td]:p-[0.2px] [&_th]:p-0">
                    <MarketTableHeading
                      title1="back"
                      title2="lay"
                      minStake={market.marketLimits?.minStake ? market.marketLimits?.minStake / cFactor : 0}
                      maxStake={market.marketLimits?.maxStake ? market.marketLimits?.maxStake / cFactor : 0}
                      hideLay={backOnly}
                    />
                    <tbody>
                      {market?.runners
                        ?.filter((bmSelection) => {
                          if (market.marketName === "Genie Combo Special Bet") {
                            return bmSelection.runnerName !== "No winner";
                          }
                          return true;
                        })
                        ?.map((runner, idx) => (
                          <BookmakerRow
                            key={idx}
                            exposureMap={exposureMap}
                            cFactor={cFactor}
                            runner={runner}
                            market={market}
                            eventData={eventData}
                            index={index}
                            setAddNewBet={setAddNewBet}
                            setBetStartTime={setBetStartTime}
                            getOpenBetsPLInArray={getOpenBetsPLInArray}
                            getTotalPL={getTotalPL}
                            hideLay={backOnly}
                          />
                        ))}

                      {market?.notification ? (
                        <tr>
                          <td colSpan={12} className=" overflow-hidden">
                            <div
                              className="marque-new"
                              style={{
                                animationDuration: `${Math.max(
                                  10,
                                  market.notification?.length / 5,
                                )}s`,
                              }}
                            >
                              <span className="notifi-mssage">
                                {market.notification}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </React.Fragment>
        );
      })}
      <Modal
        title="Do you wish to perform cash out?"
        isOpen={confirmCashout}
        onClose={() => setConfirmCashout(false)}
      >
        {/* <div className="flex">
          <Button
            color="primary"
            className="py-2 px-4 bg-orange-700 rounded text-white "
            onClick={() => setConfirmCashout(false)}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            className="py-2 px-4 ml-2 bg-green-600 rounded text-white "
            onClick={() => {
              setLoading(true);
              autoCashout();
              setConfirmCashout(false);
            }}
            disabled={loading}
          >
            {langData?.['cashout']}
          </Button>
        </div> */}
        <div className="grid grid-cols-2 w-full gap-x-2">
          {/* Cancel Button */}
          <Button
            type="button"
            onClick={() => setConfirmCashout(false)}
            className="leading-normal relative overflow-hidden transition duration-150 ease-in-out bg-transparent w-full px-2.5 py-1 border-[1px] border-primary rounded-[4px] flex items-center justify-center cursor-pointer"
          >
            <span className="text-text_Primary text-[16px] font-[700] font-lato">
              cancel
            </span>
          </Button>

          {/* Cashout Button */}
          <div className="w-full">
            <Button
              type="button"
              disabled={loading}
              onClick={() => {
                dispatch(setLoading(true));
                autoCashout();
                setConfirmCashout(false);
              }}
              className="leading-normal relative overflow-hidden transition duration-150 ease-in-out bg-[#29ab2b] w-full h-[36px] min-h-[36px] px-2.5 py-1 border-[1px] border-success rounded-[4px] flex items-center justify-center cursor-pointer"
            >
              <span className="text-white text-[16px] font-[700] font-lato">
                cashout
              </span>
            </Button>
          </div>
        </div>
      </Modal>

      {/* Turbo Cashout (Speed Cash) Modal */}
      <Modal
        title={langData?.["speed_cash"] || "Speed Cash"}
        isOpen={confirmTurboCashout}
        onClose={() => setConfirmTurboCashout(false)}
      >
        {turboCoMarket && (
          <>
            <div className="grid grid-cols-2 mb-2 border border-dashed border-black">
              {turboCoMarket.runners?.map((runner) => {
                const key = `${turboCoMarket.marketId}:${turboCoMarket.marketName}`;
                const riskRows = exposureMap[key] as
                  | { runnerId: string; runnerName: string; userRisk: number }[]
                  | undefined;
                const riskMap = new Map(
                  riskRows?.map((r) => [r.runnerId, Number(r.userRisk) || 0]) ??
                    [],
                );
                const risk = Number(riskMap.get(runner.runnerId) ?? 0);
                return (
                  <div
                    key={runner.runnerId}
                    className="text-center py-1 border-r border-dashed border-black last:border-r-0"
                  >
                    <div className="text-text_Primary text-[16px] font-[700] font-lato mb-1">
                      {runner.runnerName}
                    </div>
                    <div className="text-text_Primary text-[14px] font-[500] font-lato">
                      ₹{risk.toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-center mb-2">
              <span className="text-text_Primary text-[14px] font-[500] font-lato">
                {langData?.["speed_cash_fee"] ||
                  "We are deducting 3% fee on speed cashout."}
              </span>
            </div>
            <div className="grid grid-cols-1 w-full gap-2">
              <Button
                type="button"
                disabled={loading}
                onClick={async () => {
                  try {
                    dispatch(setLoading(true));
                    await turboCashoutFunction(turboCoMarket);
                    setConfirmTurboCashout(false);
                  } catch (error: any) {
                    dispatch(
                      showToast({
                        type: "error",
                        message:
                          error?.response?.data?.message ||
                          langData?.["speed_cash_failed"] ||
                          "Speed cash failed",
                      }),
                    );
                  } finally {
                    dispatch(setLoading(false));
                    setConfirmTurboCashout(false);
                  }
                }}
                className="leading-normal relative overflow-hidden transition duration-150 ease-in-out bg-[#5bb450] w-full min-h-[36px] px-2.5 py-3 border-[1px] border-[#5bb450] rounded-[4px] flex items-center justify-center cursor-pointer"
              >
                <span className="text-white text-[16px] font-[700] font-lato">
                  {loading
                    ? langData?.["processing"] || "Processing..."
                    : `${langData?.["speed_cash"] || "SPEED CASH"} - ₹${getTurboCashoutData(turboCoMarket)?.turboCashoutAmount?.toFixed(2) ?? "0.00"}`}
                </span>
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Bookmaker;
